import { sha256 } from '@noble/hashes/sha2.js';
import { bytesToHex, utf8 } from './bytes.ts';

/**
 * `y-canon-v2`, copied rule for rule from backend-y-new
 * `apps/backend/src/provenance/concordium/canon.ts`, so the SHA-256 computed
 * here (in Node or on the phone) is the `h` y.dk registered on Concordium.
 *
 *  - UTF-8 JSON, object keys sorted by UTF-16 code unit (`Array#sort`), no
 *    whitespace between tokens;
 *  - every string (keys too) NFC-normalized, `\r\n` and lone `\r` -> `\n`;
 *  - array order kept;
 *  - only strings, safe integers, booleans, null, arrays and plain objects;
 *    anything else is refused; an object key whose value is `undefined` is
 *    left out (as JSON.stringify does).
 */
export const CANON_V2 = 'y-canon-v2';

export function normalizeText(text: string): string {
  return text.replace(/\r\n?/g, '\n').normalize('NFC');
}

export function canonicalize(value: unknown): string {
  return write(value, '$');
}

export function sha256Hex(data: string | Uint8Array): string {
  return bytesToHex(sha256(typeof data === 'string' ? utf8(data) : data));
}

/** The canonical string and its SHA-256: what a registration is made of. */
export function canonicalHash(value: unknown): { canonical: string; sha256: string } {
  const canonical = canonicalize(value);
  return { canonical, sha256: sha256Hex(canonical) };
}

function write(value: unknown, path: string): string {
  if (value === null) return 'null';
  switch (typeof value) {
    case 'string':
      return JSON.stringify(normalizeText(value));
    case 'boolean':
      return value ? 'true' : 'false';
    case 'number':
      if (!Number.isSafeInteger(value)) throw new Error(`${CANON_V2}: ${path} is not a safe integer`);
      return String(value);
    case 'object': {
      if (Array.isArray(value)) {
        return `[${value.map((v, i) => write(v, `${path}[${i}]`)).join(',')}]`;
      }
      const proto = Object.getPrototypeOf(value) as unknown;
      if (proto !== Object.prototype && proto !== null) {
        throw new Error(`${CANON_V2}: ${path} is not a plain object`);
      }
      const record = value as Record<string, unknown>;
      const keys = Object.keys(record).map((k) => normalizeText(k));
      if (new Set(keys).size !== keys.length) throw new Error(`${CANON_V2}: ${path} has keys equal after NFC`);
      const entries = Object.keys(record)
        .map((k) => [normalizeText(k), record[k]] as const)
        .filter(([, v]) => v !== undefined)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
      return `{${entries.map(([k, v]) => `${JSON.stringify(k)}:${write(v, `${path}.${k}`)}`).join(',')}}`;
    }
    default:
      throw new Error(`${CANON_V2}: ${path} has unsupported type ${typeof value}`);
  }
}
